import React, { useState, useEffect, useRef } from 'react';
import { MasterData, DayWiseDetail } from '../types';
import { itineraryService } from '../services/itinerary';
import api from '../services/api';
import BasicInfoForm from './form/BasicInfoForm';
import AccommodationsForm from './form/AccommodationsForm';
import DayWiseForm from './form/DayWiseForm';
import AdditionalActivitiesForm from './form/AdditionalActivitiesForm';
import ExtraExpensesForm from './form/ExtraExpensesForm';

interface Props {
  itinerary: any | null;
  action: 'create' | 'edit' | 'copy';
  masterData: MasterData;
  onClose: () => void;
}

const ItineraryFormModal: React.FC<Props> = ({ itinerary, action, masterData, onClose }) => {
  // Normalize itinerary data - backend sends 'date' but we use 'tour_date'
  const normalizedItinerary = itinerary ? {
    ...itinerary,
    day_wise_details: itinerary.day_wise_details?.map((day: any, index: number) => {
      // Calculate tour_date if missing or empty
      let tourDate = day.tour_date || day.date || '';
      if (!tourDate && itinerary.basic_info?.travel_date) {
        const baseDate = new Date(itinerary.basic_info.travel_date);
        baseDate.setDate(baseDate.getDate() + index);
        tourDate = baseDate.toISOString().split('T')[0];
      }
      return {
        ...day,
        tour_date: tourDate
      };
    })
  } : null;

  const getDefaultFormData = () => {
    const travelDate = new Date().toISOString().split('T')[0];
    const noOfDays = 4;
    const checkoutDate = new Date();
    checkoutDate.setDate(checkoutDate.getDate() + noOfDays);
    
    // Auto-calculate water expense when creating new itinerary
    const waterKey = "Water (1 Per Pax Per Day)";
    const waterCost = 2 * noOfDays * 3.5; // Default 2 pax - will be recalculated by backend on save
    
    return {
      itinerary_id: -1,
      basic_info: {
        agency_id: -1,
        agency_name: '',
        itinerary_name: '',
        lead_guest: '',
        travel_date: travelDate,
        no_of_pax: 2,
        no_of_children: 0,
        no_of_days: noOfDays,
        vehicle_type_id: -1,
        vehicle_type: '',
        children_ages: [],
        created_by: '',
        created_date: new Date().toISOString().split('T')[0],
        last_updated_by: '',
        last_updated_date: new Date().toISOString().split('T')[0]
      },
      accommodations: [
        {
          hotel_id: -1,
          hotel_name: '',
          room_id: -1,
          room_type: '',
          category: '3 Star',
          checkin_date: travelDate,
          checkout_date: checkoutDate.toISOString().split('T')[0],
          no_of_rooms: 1,
          no_of_nights: noOfDays - 1,
          no_of_extra_beds: 0,
          no_kids_for_bf: 0,
          foc: 0
        }
      ],
      day_wise_details: [],
      additional_activities: [],
      extra_expenses: [{ expense: waterKey, cost: waterCost }],
      remarks: '',
      operation_team_remarks: '',
      trip_type: 'FIT',
      guest_type: 'FIT',
      mup: 450,
      perc: 1.25,
      itinerary_currency: 'INR',
      status: 'PENDING_CONFIRMATION',
      itinerary_version: 3
    };
  };

  const [formData, setFormData] = useState<any>(normalizedItinerary || getDefaultFormData());

  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [errorNotification, setErrorNotification] = useState<string | null>(null);
  const [showNyepiAlert, setShowNyepiAlert] = useState(false);
  const [nyepiYear, setNyepiYear] = useState<number | null>(null);
  const [accommodationHotelDetails, setAccommodationHotelDetails] = useState<Record<number, any>>({});

  // Log when accommodation hotel details change
  useEffect(() => {
    console.log('ItineraryFormModal - accommodationHotelDetails updated:', accommodationHotelDetails);
  }, [accommodationHotelDetails]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.overscrollBehavior = 'auto';
    };
  }, []);

  useEffect(() => {
    // Update first accommodation dates when travel_date or no_of_days changes
    if (formData.accommodations.length > 0) {
      const travelDate = formData.basic_info.travel_date;
      const noOfDays = formData.basic_info.no_of_days;
      const checkoutDate = new Date(travelDate);
      checkoutDate.setDate(checkoutDate.getDate() + noOfDays);
      
      const updatedAccommodations = [...formData.accommodations];
      updatedAccommodations[0] = {
        ...updatedAccommodations[0],
        checkin_date: travelDate,
        checkout_date: checkoutDate.toISOString().split('T')[0],
        no_of_nights: noOfDays - 1
      };
      
      setFormData({ ...formData, accommodations: updatedAccommodations });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.basic_info.travel_date, formData.basic_info.no_of_days]);

  const waterDebounceRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    // Fetch water cost from backend when pax/days/children change
    const waterKey = "Water (1 Per Pax Per Day)";
    if (waterDebounceRef.current) clearTimeout(waterDebounceRef.current);
    waterDebounceRef.current = setTimeout(async () => {
      try {
        const ages = formData.basic_info.children_ages || [];
        const res = await api.get('/itineraries/water-cost', {
          params: {
            no_of_pax: formData.basic_info.no_of_pax,
            no_of_days: formData.basic_info.no_of_days,
            children_ages: JSON.stringify(ages)
          }
        });
        const waterCost = res.data.water_cost;
        setFormData((prev: any) => {
          const existing = prev.extra_expenses.find((e: any) => e.expense === waterKey);
          if (!existing) {
            return { ...prev, extra_expenses: [{ expense: waterKey, cost: waterCost }, ...prev.extra_expenses] };
          }
          if (existing.cost === waterCost) return prev;
          return { ...prev, extra_expenses: prev.extra_expenses.map((e: any) => e.expense === waterKey ? { ...e, cost: waterCost } : e) };
        });
      } catch {}
    }, 400);
    return () => { if (waterDebounceRef.current) clearTimeout(waterDebounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.basic_info.no_of_pax, formData.basic_info.no_of_days, formData.basic_info.children_ages]);

  useEffect(() => {
    // Auto-calculate day-wise details based on travel_date and no_of_days (like Streamlit)
    const currentDays = formData.day_wise_details.length;
    const requiredDays = formData.basic_info.no_of_days;
    const travelDate = formData.basic_info.travel_date;
    
    if (currentDays !== requiredDays || travelDate) {
      const days: DayWiseDetail[] = [];
      
      // Always regenerate all days to ensure dates are correct
      for (let i = 0; i < requiredDays; i++) {
        const existingDay = formData.day_wise_details[i];
        const dayDate = new Date(travelDate);
        dayDate.setDate(dayDate.getDate() + i);
        
        days.push({
          day: i + 1,
          tour_date: dayDate.toISOString().split('T')[0], // Always auto-calculate
          tour_id: existingDay?.tour_id || -1,
          tour_name: existingDay?.tour_name || '',
          is_custom_tour: existingDay?.is_custom_tour || false,
          lunch_required: existingDay?.lunch_required || false,
          dinner_required: existingDay?.dinner_required || false,
          lunch: existingDay?.lunch,
          dinner: existingDay?.dinner,
          activities: existingDay?.activities || [],
          day_addons: existingDay?.day_addons || [],
          day_extras: existingDay?.day_extras || [],
          custom_trip_type_id: existingDay?.custom_trip_type_id,
          custom_trip_type: existingDay?.custom_trip_type,
          custom_tour_description: existingDay?.custom_tour_description,
          auto_populate_description: existingDay?.auto_populate_description
        });
      }
      
      setFormData({ ...formData, day_wise_details: days });
    }
  }, [formData.basic_info.no_of_days, formData.basic_info.travel_date]);

  const validateItinerary = () => {
    // 1) Select Agency
    if (formData.basic_info.agency_id === -1) {
      setErrorNotification('Please select an agency');
      setActiveTab('basic');
      return false;
    }
    
    // 2) Itinerary name
    if (!formData.basic_info.itinerary_name || formData.basic_info.itinerary_name.trim() === '') {
      setErrorNotification('Please enter an itinerary name');
      setActiveTab('basic');
      return false;
    }
    
    // 3) Vehicle Type
    if (formData.basic_info.vehicle_type_id === -1) {
      setErrorNotification('Please select a vehicle type');
      setActiveTab('basic');
      return false;
    }
    
    // 4) No of days (minimum 4)
    if (formData.basic_info.no_of_days < 4) {
      setErrorNotification('Number of days must be at least 4');
      setActiveTab('basic');
      return false;
    }
    
    // 5) For every accommodation - validate dates and hotel/room selection
    const travelDate = new Date(formData.basic_info.travel_date);
    for (let i = 0; i < formData.accommodations.length; i++) {
      const acc = formData.accommodations[i];
      
      // Hotel and room must be selected
      if (acc.hotel_id === -1) {
        setErrorNotification(`Accommodation ${i + 1}: Please select a hotel`);
        setActiveTab(hasFamilies ? 'accommodations-advanced' : 'accommodations');
        return false;
      }
      
      // Check room selection - either top-level room_id or room_details configs
      const hasRoomDetails = (acc as any).room_details?.configs?.length > 0;
      if (hasRoomDetails) {
        // Advanced mode: validate each room config has a room type
        const configs = (acc as any).room_details.configs;
        for (let j = 0; j < configs.length; j++) {
          if (!configs[j].room_id) {
            setErrorNotification(`Accommodation ${i + 1}, Room Config ${j + 1}: Please select a room type`);
            setActiveTab('accommodations-advanced');
            return false;
          }
        }
        
        // Advanced mode: validate all families are assigned to at least one room config
        if (formData.basic_info?.families && formData.basic_info.families.length > 0) {
          const assignedFamilyIds = new Set(configs.map((c: any) => c.family_id).filter(Boolean));
          const unassignedFamilies = formData.basic_info.families.filter((f: any) => !assignedFamilyIds.has(f.family_id));
          if (unassignedFamilies.length > 0) {
            const names = unassignedFamilies.map((f: any) => `Family ${f.family_id} (${f.no_of_pax}A + ${f.no_of_kids || 0}C)`).join(', ');
            setErrorNotification(`Accommodation ${i + 1}: ${unassignedFamilies.length} family/families not assigned to any room: ${names}`);
            setActiveTab('accommodations-advanced');
            return false;
          }

          // Validate guest assignments within assigned families
          for (let j = 0; j < configs.length; j++) {
            const config = configs[j];
            if (config.family_id) {
              const family = formData.basic_info.families.find((f: any) => f.family_id === config.family_id);
              if (family) {
                const availableGuests: any[] = [];
                for (let k = 0; k < family.no_of_pax; k++) {
                  availableGuests.push({ is_adult: true });
                }
                family.kids_ages?.forEach((age: number) => {
                  availableGuests.push({ is_adult: false, age });
                });
                
                const assignedGuests: any[] = [];
                configs.forEach((c: any) => {
                  if (c.family_id === config.family_id) {
                    if (c.guests_in_room) assignedGuests.push(...c.guests_in_room);
                    if (c.extra_bed_guests) assignedGuests.push(...c.extra_bed_guests);
                    if (c.kids_bf_guests) assignedGuests.push(...c.kids_bf_guests);
                    if (c.kids_foc_guests) assignedGuests.push(...c.kids_foc_guests);
                  }
                });
                
                const remaining = [...availableGuests];
                assignedGuests.forEach(assignedGuest => {
                  const idx = remaining.findIndex(g => 
                    g.is_adult === assignedGuest.is_adult && g.age === assignedGuest.age
                  );
                  if (idx !== -1) remaining.splice(idx, 1);
                });
                
                if (remaining.length > 0) {
                  setErrorNotification(`Accommodation ${i + 1}: ${remaining.length} guest(s) from Family ${config.family_id} are not assigned to rooms. Please assign all guests.`);
                  setActiveTab('accommodations-advanced');
                  return false;
                }
              }
            }
          }
        }
      } else {
        // Simple mode: validate top-level room_id
        if (!acc.room_id || acc.room_id === -1) {
          setErrorNotification(`Accommodation ${i + 1}: Please select a room type`);
          setActiveTab(hasFamilies ? 'accommodations-advanced' : 'accommodations');
          return false;
        }
      }
      
      // Validate extra bed cost if extra beds are selected
      if (acc.no_of_extra_beds > 0) {
        // Skip validation for hotels starting with "Property booked by Guest" (case and space insensitive)
        const normalizedHotelName = acc.hotel_name?.toLowerCase().replace(/\s+/g, '');
        const isGuestBookedProperty = normalizedHotelName?.startsWith('propertybookedbyguest');
        
        if (!isGuestBookedProperty) {
          const hotelDetails = accommodationHotelDetails[i];
          console.log(`Accommodation ${i + 1} validation:`, {
            no_of_extra_beds: acc.no_of_extra_beds,
            hotelDetails,
            extra_bed_cost: hotelDetails?.extra_bed_cost
          });
          if (hotelDetails && (hotelDetails.extra_bed_cost === 0 || hotelDetails.extra_bed_cost === null)) {
            setErrorNotification(`Accommodation ${i + 1}: Extra bed cost is 0 for ${acc.hotel_name}. Please verify with hotel or remove extra beds.`);
            setActiveTab(hasFamilies ? 'accommodations-advanced' : 'accommodations');
            return false;
          }
        }
      }
      
      // Checkin date must be >= travel date
      const checkinDate = new Date(acc.checkin_date);
      if (checkinDate < travelDate) {
        setErrorNotification(`Accommodation ${i + 1}: Check-in date cannot be before travel date`);
        setActiveTab(hasFamilies ? 'accommodations-advanced' : 'accommodations');
        return false;
      }
      
      // Checkout date must be > checkin date
      const checkoutDate = new Date(acc.checkout_date);
      if (checkoutDate <= checkinDate) {
        setErrorNotification(`Accommodation ${i + 1}: Check-out date must be after check-in date`);
        setActiveTab(hasFamilies ? 'accommodations-advanced' : 'accommodations');
        return false;
      }
      
      // Checkout date must be <= itinerary end date (travel_date + no_of_days)
      const itineraryEndDate = new Date(formData.basic_info.travel_date);
      itineraryEndDate.setDate(itineraryEndDate.getDate() + formData.basic_info.no_of_days);
      if (checkoutDate > itineraryEndDate) {
        setErrorNotification(`Accommodation ${i + 1}: Check-out date cannot be after itinerary end date (${itineraryEndDate.toISOString().split('T')[0]})`);
        setActiveTab('accommodations');
        return false;
      }
    }
    
    // 6) Total accommodation nights must equal (no_of_days - 1)
    const totalNights = formData.accommodations.reduce((sum: number, acc: any) => sum + acc.no_of_nights, 0);
    const expectedNights = formData.basic_info.no_of_days - 1;
    if (totalNights !== expectedNights) {
      setErrorNotification(`Total accommodation nights (${totalNights}) must equal ${expectedNights} nights (${formData.basic_info.no_of_days} days - 1)`);
      setActiveTab('accommodations');
      return false;
    }
    
    // 7) For every day wise - validate tour or custom tour
    for (let i = 0; i < formData.day_wise_details.length; i++) {
      const day = formData.day_wise_details[i];
      
      if (day.is_custom_tour) {
        // Custom tour validation
        if (!day.tour_name || day.tour_name.trim() === '') {
          setErrorNotification(`Day ${i + 1}: Please enter a custom tour name`);
          setActiveTab('daywise');
          return false;
        }
        
        if (day.custom_trip_type_id === -1 || !day.custom_trip_type) {
          setErrorNotification(`Day ${i + 1}: Please select a trip type for custom tour`);
          setActiveTab('daywise');
          return false;
        }
      } else {
        // Regular tour validation
        if (day.tour_id === -1) {
          setErrorNotification(`Day ${i + 1}: Please select a tour or enable custom tour`);
          setActiveTab('daywise');
          return false;
        }
      }
      
      // 8) Meal validation
      if (day.lunch_required && (!day.lunch || day.lunch.meal_id === -1)) {
        setErrorNotification(`Day ${i + 1}: Please select a lunch meal`);
        setActiveTab('daywise');
        return false;
      }
      
      if (day.dinner_required && (!day.dinner || day.dinner.meal_id === -1)) {
        setErrorNotification(`Day ${i + 1}: Please select a dinner meal`);
        setActiveTab('daywise');
        return false;
      }
      
      // 11) Day activities validation
      if (day.day_activities && day.day_activities.length > 0) {
        for (let j = 0; j < day.day_activities.length; j++) {
          const activity = day.day_activities[j];
          if (activity.activity_id === -1 || !activity.calculated_adults || activity.calculated_adults <= 0) {
            setErrorNotification(`Day ${i + 1}: Day activity ${j + 1} must have a valid activity with count > 0`);
            setActiveTab('daywise');
            return false;
          }
        }
      }
      
      // 12) Day extras validation - validate if extras are added
      if (day.day_extras && day.day_extras.length > 0) {
        for (let j = 0; j < day.day_extras.length; j++) {
          const extra = day.day_extras[j];
          if (!extra.extra_text || extra.extra_text.trim() === '') {
            setErrorNotification(`Day ${i + 1}: Day extra ${j + 1} must have extra text`);
            setActiveTab('daywise');
            return false;
          }
          if (!extra.rate || extra.rate <= 0) {
            setErrorNotification(`Day ${i + 1}: Day extra ${j + 1} must have a valid rate > 0`);
            setActiveTab('daywise');
            return false;
          }
          if (!extra.number || extra.number <= 0) {
            setErrorNotification(`Day ${i + 1}: Day extra ${j + 1} must have a valid number > 0`);
            setActiveTab('daywise');
            return false;
          }
        }
      }
    }
    
    // 9) Additional activities validation
    if (formData.additional_activities && formData.additional_activities.length > 0) {
      for (let i = 0; i < formData.additional_activities.length; i++) {
        const activity = formData.additional_activities[i];
        if (activity.activity_id === -1) {
          setErrorNotification(`Additional Activity ${i + 1}: Please select a valid activity`);
          setActiveTab('activities');
          return false;
        }
      }
    }
    
    // 10) Extra expenses validation - only validate if expenses exist
    if (formData.extra_expenses && formData.extra_expenses.length > 0) {
      for (let i = 0; i < formData.extra_expenses.length; i++) {
        const expense = formData.extra_expenses[i];
        if (!expense.expense || expense.expense.trim() === '') {
          setErrorNotification(`Extra Expense ${i + 1}: Please enter an expense name`);
          setActiveTab('expenses');
          return false;
        }
        if (!expense.cost || expense.cost <= 0) {
          setErrorNotification(`Extra Expense ${i + 1}: Please enter a valid cost (greater than 0)`);
          setActiveTab('expenses');
          return false;
        }
      }
    }
    
    // Nyepi Day validation - check if March 19 falls within the itinerary dates for any year
    const checkNyepiDay = () => {
      const travelStartDate = new Date(formData.basic_info.travel_date);
      const travelEndDate = new Date(travelStartDate);
      travelEndDate.setDate(travelEndDate.getDate() + formData.basic_info.no_of_days - 1);
      
      // Check each year that the itinerary spans
      const startYear = travelStartDate.getFullYear();
      const endYear = travelEndDate.getFullYear();
      
      for (let year = startYear; year <= endYear; year++) {
        const nyepiDate = new Date(`${year}-03-19`);
        
        // Check if Nyepi Day falls within the travel period
        if (nyepiDate >= travelStartDate && nyepiDate <= travelEndDate) {
          setNyepiYear(year);
          setShowNyepiAlert(true);
          return false; // Stop validation until user confirms
        }
      }
      return true;
    };
    
    if (!checkNyepiDay()) {
      return false;
    }
    
    return true;
  };

  const handleSave = async (skipNyepiCheck = false, forceConfirm = false): Promise<void> => {
    try {
      // Frontend validation (including Nyepi Day check)
      if (!skipNyepiCheck && !validateItinerary()) {
        return;
      }
      
      setSaving(true);
      console.log('=== SAVING TO BACKEND ===');
      console.log('forceConfirm parameter:', forceConfirm);
      console.log('Original formData.additional_activities:', formData.additional_activities);
      
      // Clean up meal data and fix accommodation room_id for advanced mode
      const cleanedFormData = {
        ...formData,
        force_save: forceConfirm || false, // Add force_save flag for backend validation bypass
        accommodations: formData.accommodations.map((acc: any) => {
          // If has room_details configs, set top-level room from first config
          if (acc.room_details?.configs?.length > 0 && acc.room_details.configs[0].room_id) {
            return {
              ...acc,
              room_id: acc.room_details.configs[0].room_id,
              room_type: acc.room_details.configs[0].room_type
            };
          }
          return acc;
        }),
        day_wise_details: formData.day_wise_details.map((day: any) => ({
          ...day,
          lunch: day.lunch_required ? day.lunch : undefined,
          dinner: day.dinner_required ? day.dinner : undefined
        }))
      };
      
      // Remove smart breakdowns and price data - backend will recalculate them
      delete cleanedFormData.given_smart_breakdown;
      delete cleanedFormData.system_smart_breakdown;
      delete cleanedFormData.cost_summary;
      delete cleanedFormData.cost_breakdown;
      delete cleanedFormData.given_price; // Remove given_price to avoid validation errors
      delete cleanedFormData.system_price;
      delete cleanedFormData.system_recommended_price;
      delete cleanedFormData.group_rate_card;
      
      console.log('Cleaned formData.additional_activities:', cleanedFormData.additional_activities);
      console.log('force_save being sent to backend:', cleanedFormData.force_save);
      console.log('Additional activities count:', cleanedFormData.additional_activities?.length || 0);
      if (cleanedFormData.additional_activities?.length > 0) {
        cleanedFormData.additional_activities.forEach((act: any, i: number) => {
          console.log(`Activity ${i + 1}:`, {
            activity_id: act.activity_id,
            activity_name: act.activity_name,
            calculated_adults: act.calculated_adults
          });
        });
      }
      
      let result;
      if (action === 'edit') {
        console.log('Calling updateItinerary with ID:', formData.itinerary_id);
        result = await itineraryService.updateItinerary(formData.itinerary_id, cleanedFormData);
      } else {
        console.log('Calling createItinerary');
        // If this is a Create Version flow, pass version_of so backend links to same quotation
        if (itinerary?._version_of) {
          cleanedFormData.version_of = itinerary._version_of;
        }
        result = await itineraryService.createItinerary(cleanedFormData);
      }
      
      // Check if backend requires confirmation
      if (result?.requires_confirmation && result?.validation) {
        console.log('Backend requires confirmation:', result.validation);
        
        // Show confirmation dialog
        const confirmationMessages = [];
        if (result.validation.warnings) {
          confirmationMessages.push(...result.validation.warnings.map((w: any) => w.message));
        }
        if (result.validation.confirmations_needed) {
          confirmationMessages.push(...result.validation.confirmations_needed.map((c: any) => c.message));
        }
        
        const confirmMessage = confirmationMessages.join('\n\n');
        const userConfirmed = window.confirm(`${result.message || 'Validation Required'}\n\n${confirmMessage}\n\nDo you want to continue?`);
        
        if (userConfirmed) {
          // Retry with force_save = true
          console.log('User confirmed, retrying with force_save=true');
          setSaving(false);
          return handleSave(skipNyepiCheck, true);
        } else {
          console.log('User cancelled confirmation');
          setSaving(false);
          return;
        }
      }
      
      console.log('=== SAVE ITINERARY SUCCESS ===');
      onClose();
    } catch (error: any) {
      console.error('=== SAVE ITINERARY ERROR ===');
      console.error('Error object:', error);
      console.error('Error message:', error?.message);
      console.error('Error response:', error?.response);
      console.error('Error response data:', error?.response?.data);
      console.error('Error response status:', error?.response?.status);
      console.error('Error response headers:', error?.response?.headers);
      
      let errorMessage = 'Error saving itinerary. Please try again.';
      
      if (error?.response?.data) {
        const errorData = error.response.data;
        
        // Handle different error response formats
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (Array.isArray(errorData)) {
          // Handle validation errors array
          errorMessage = errorData.map(err => err.message || err.msg || err).join(', ');
        } else {
          // Try to extract meaningful error info
          errorMessage = JSON.stringify(errorData).substring(0, 200);
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      // Add status code to error message for debugging
      if (error?.response?.status) {
        errorMessage = `[${error.response.status}] ${errorMessage}`;
      }
      
      setErrorNotification(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const hasFamilies = formData.basic_info?.families && formData.basic_info.families.length > 0;
  
  console.log('🔍 Tab visibility debug:', {
    hasFamilies,
    familiesArray: formData.basic_info?.families,
    familiesLength: formData.basic_info?.families?.length
  });

  const tabs = [
    { id: 'basic', label: '📋 Basic Info', icon: '📋', show: true },
    { id: 'accommodations', label: '🏨 Accommodations', icon: '🏨', show: !hasFamilies },
    { id: 'accommodations-advanced', label: '🏨 Accommodations (Advanced)', icon: '🏨', show: hasFamilies },
    { id: 'daywise', label: '📅 Day-wise Details', icon: '📅', show: true },
    { id: 'activities', label: '🎯 Additional Activities', icon: '🎯', show: true },
    { id: 'expenses', label: '💰 Extra Expenses', icon: '💰', show: true }
  ].filter(tab => tab.show === true);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:w-[90vw] sm:h-[90vh] sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 py-3">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-bold">
                {action === 'create' ? '➕ Create' : action === 'edit' ? '✏️ Edit' : itinerary?._version_of ? '🔀 Create Version' : '📋 Copy'} Itinerary
              </h2>
              {action === 'edit' && (formData.wyn_itinerary_id || formData.id || formData.itinerary_id) && formData.basic_info?.itinerary_name && (
                <p className="text-white text-opacity-90 text-sm">
                  <span className="font-mono font-bold">{formData.wyn_itinerary_id || `WH_${formData.id || formData.itinerary_id}`}</span>
                  <span className="mx-2">•</span>
                  <span className="font-medium">{formData.basic_info.itinerary_name}</span>
                </p>
              )}
              {action === 'copy' && itinerary?._version_of && (
                <p className="text-white text-opacity-90 text-sm">
                  <span className="text-xs opacity-75">Based on: </span>
                  <span className="font-mono font-bold">
                    {itinerary.quotation_number ? `${itinerary.quotation_number}/${itinerary.version_number}` : `WH_${itinerary._version_of}`}
                  </span>
                  {itinerary.basic_info?.itinerary_name && (
                    <><span className="mx-2">•</span><span className="font-medium">{itinerary.basic_info.itinerary_name}</span></>
                  )}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1.5 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-3 sm:px-6 sm:py-4 font-medium text-sm sm:text-base whitespace-nowrap transition-colors flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'border-b-2 border-primary-600 text-primary-600 bg-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 pb-12 min-h-0">
          {activeTab === 'basic' && (
            <BasicInfoForm
              basicInfo={formData.basic_info}
              perc={formData.perc}
              additional={formData.additional}
              currency={formData.itinerary_currency}
              tripType={formData.trip_type}
              guestType={formData.guest_type}
              mup={formData.mup}
              masterData={masterData}
              onChange={(updates) => setFormData({ ...formData, ...updates })}
            />
          )}

          {activeTab === 'accommodations' && (
            <AccommodationsForm
              accommodations={formData.accommodations}
              travelDate={formData.basic_info.travel_date}
              noOfDays={formData.basic_info.no_of_days}
              noOfChildren={formData.basic_info.no_of_children}
              noOfPax={formData.basic_info.no_of_pax}
              childrenAges={formData.basic_info.children_ages}
              families={formData.basic_info.families}
              onChange={(accommodations) => setFormData({ ...formData, accommodations })}
              onHotelDetailsChange={(details) => setAccommodationHotelDetails(details)}
              forceSimpleMode={true}
            />
          )}

          {activeTab === 'accommodations-advanced' && (
            <AccommodationsForm
              accommodations={formData.accommodations}
              travelDate={formData.basic_info.travel_date}
              noOfDays={formData.basic_info.no_of_days}
              noOfChildren={formData.basic_info.no_of_children}
              noOfPax={formData.basic_info.no_of_pax}
              childrenAges={formData.basic_info.children_ages}
              families={formData.basic_info.families}
              onChange={(accommodations) => setFormData({ ...formData, accommodations })}
              onHotelDetailsChange={(details) => setAccommodationHotelDetails(details)}
              forceAdvancedMode={true}
            />
          )}

          {activeTab === 'daywise' && (
            <DayWiseForm
              dayWiseDetails={formData.day_wise_details}
              basicInfo={formData.basic_info}
              accommodations={formData.accommodations}
              masterData={masterData}
              onChange={(day_wise_details) => setFormData({ ...formData, day_wise_details })}
            />
          )}

          {activeTab === 'activities' && (
            <AdditionalActivitiesForm
              activities={formData.additional_activities}
              masterData={masterData}
              onChange={(additional_activities) => setFormData({ ...formData, additional_activities })}
            />
          )}

          {activeTab === 'expenses' && (
            <ExtraExpensesForm
              expenses={formData.extra_expenses}
              remarks={formData.remarks}
              operationRemarks={formData.operation_team_remarks}
              onChange={(updates) => setFormData({ ...formData, ...updates })}
            />
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 sm:p-6 bg-gray-50 flex-shrink-0">
          <div className="flex justify-end gap-4">
            <button onClick={onClose} className="btn-secondary px-8">
              Cancel
            </button>
            <button
              onClick={() => handleSave()}
              disabled={saving}
              className="btn-primary px-8 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : action === 'edit' ? 'Update' : 'Create'} Itinerary
            </button>
          </div>
        </div>
      </div>

      {/* Error Notification Toast */}
      {errorNotification && (
        <div className="fixed top-4 right-4 z-[70] animate-slide-in">
          <div className="rounded-lg shadow-lg p-4 max-w-md bg-red-50 border border-red-200">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">
                  {errorNotification}
                </p>
              </div>
              <button onClick={() => setErrorNotification(null)} className="flex-shrink-0 text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Nyepi Day Alert Modal */}
      {showNyepiAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white text-center">
              <div className="text-4xl mb-2">🚨</div>
              <h3 className="text-xl font-bold">NYEPI DAY ALERT</h3>
              <p className="text-orange-100 text-sm mt-1">Balinese New Year</p>
            </div>
            
            <div className="p-6">
              <div className="text-center mb-4">
                <p className="text-lg font-semibold text-gray-800 mb-2">
                  March 19, {nyepiYear} falls within your itinerary dates
                </p>
                <p className="text-sm text-gray-600">
                  This is Nyepi Day (Balinese New Year)
                </p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-amber-800 mb-2 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Important Reminders
                </h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Communicate with Agency about Nyepi restrictions</li>
                  <li>• Check Ops team for special Nyepi packages</li>
                  <li>• No activities or transportation available</li>
                  <li>• Guests must stay at accommodation</li>
                </ul>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowNyepiAlert(false);
                    setNyepiYear(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowNyepiAlert(false);
                    setNyepiYear(null);
                    // Continue with save after confirmation
                    handleSave(true);
                  }}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-colors font-medium"
                >
                  Continue Anyway
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItineraryFormModal;
